import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg || JSON.stringify(e))).join(' ');
  return String(detail);
}

export default function Register() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
      setUser(res.data);
      // Navigation handled by useEffect watching `user`
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8 sm:p-10">
          <div className="text-center mb-8">
            <GraduationCap className="w-10 h-10 text-cabernet mx-auto mb-4" />
            <h1 className="text-3xl font-heading font-medium text-cabernet mb-2">Create Account</h1>
            <p className="font-body text-sm text-ink/60">
              Join GoodLiving Scholars to apply for scholarships
            </p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-3 mb-5">
            {error && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm font-accent text-red-700" data-testid="register-error">
                {error}
              </div>
            )}
            <div>
              <Input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                data-testid="register-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm py-5"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="flex items-center gap-3 mb-5">
            <Separator className="flex-1" />
            <span className="font-accent text-xs text-ink/40 uppercase">or</span>
            <Separator className="flex-1" />
          </div>

          <Button
            onClick={handleGoogleLogin}
            className="w-full bg-white border-2 border-ink/10 text-ink hover:bg-ink/5 font-accent text-sm py-5 flex items-center justify-center gap-3"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center text-sm font-body text-ink/60 mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-cabernet font-semibold hover:underline" data-testid="go-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
